import TablaLogo from "@assets/TablaLogo.svg";
import { HEADER_MAIN_MENU } from "@config/HeaderMenuConstants";
import { AdsLb1 } from "@elements/Advertisement/variant/AdsLb";
import { TopHeader } from "@header/TopHeader/TopHeader";
import { ArticleAdSlotTargetingType, PageAdTargetValue } from "@typings/Ads.d";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { HamburgerMenuModal } from "./HamburgerMenu/HamburgerMenuModel";
import { HeaderLeftWrapper } from "./HeaderLeftWrapper";
import { HeaderMenuList } from "./HeaderMenuList";
import { SearchInput } from "./Search/SearchInput";

export interface HeaderProps {
  testId?: string;
  className?: string;
  pageAdTargetValue: PageAdTargetValue;
  searchedText?: string;
  gsChannels?: string[];
  sectionName: string;
  bigHeader?: boolean;
  articleSlotTarget?: ArticleAdSlotTargetingType[];
}

export function Header({
  testId = "header",
  className,
  pageAdTargetValue,
  searchedText = "",
  gsChannels,
  sectionName,
  bigHeader = false,
  articleSlotTarget,
}: HeaderProps) {
  // Search button.
  const [expanded, setExpanded] = useState(searchedText ? true : false);
  const toggleExpanded = () => {
    setExpanded((prevExpanded) => !prevExpanded);
    // Make hamburger menu hidden if search bar is open.
    setShowMenuIcon(true);
    setExpandMenu(false);
  };

  // Hamburger menu.
  const [showMenuIcon, setShowMenuIcon] = useState(true);
  const [expandMenu, setExpandMenu] = useState(false);
  const toggleMobileMenu = () => {
    setShowMenuIcon((prevShowMenuIcon) => !prevShowMenuIcon);
    setExpandMenu((prevExpandMenu) => !prevExpandMenu);

    // Make search bar is hidden if hamburger menu is open.
    setExpanded(false);
  };

  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      const adElement = document.querySelector(".header-ad");
      if (!adElement) return;

      const adScollingWrpper = adElement.getBoundingClientRect();
      const isAtTop = window.scrollY === 0;

      if (isAtTop) {
        setIsScrolled(false);
      } else if (adScollingWrpper.bottom <= 0) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  return (
    <>
      <div
        data-testid={`${testId}-headerContainer`}
        className={twMerge("bg-grey-200", className, isScrolled && "pb-xxl")}
      >
        {/* Top header LB1 ads */}
        <div
          className={twMerge(
            "header-ad bg-grey-500 xmd:relative",
            expandMenu && "hidden",
          )}
        >
          <AdsLb1
            sectionName={sectionName}
            pageAdTargetType={pageAdTargetValue}
            gsChannels={gsChannels}
            articleSlotTarget={articleSlotTarget}
          />
        </div>

        {/* Top header */}
        <TopHeader
          className={twMerge(
            "hidden md:block w-full mx-auto",
            isScrolled && "hidden",
          )}
        />

        {/* Header with search logo and main menu */}
        <div
          className={twMerge(
            "bg-grey-200 mt-3 xmd:mt-5 transition-transform duration-1000 ease-in-out",
            isScrolled
              ? "fixed top-0 z-50 w-full lg:translate-y-0"
              : "lg:relative lg:-translate-y-full lg:inline",
            (isScrolled || expandMenu) && "!mt-0 h-xxl",
            !bigHeader && "h-xxl w-full lg:mt-0",
          )}
        >
          <div
            className={twMerge(
              "lg:max-w-7xl md:px-4 lg:px-2.5 xmd:justify-between lg:mx-auto md:block lg:flex items-center pb-2 xmd:pb-4",
              (!bigHeader || isScrolled) && "h-xxl !pb-0",
              (!bigHeader || isScrolled || expandMenu) &&
                "inline md:block lg:flex !pb-0",
              bigHeader && expanded && "lg:pb-1.5",
              bigHeader && !isScrolled && "lg:pt-4",
            )}
          >
            <div
              className={twMerge(
                "inline-flex px-4 md:px-0 justify-between mb-2 xmd:mb-0 w-full",
                bigHeader && "xmd:float-left  lg:w-auto",
                (!bigHeader || isScrolled || expandMenu) &&
                  "pt-3 pb-2.5 h-[57px] items-center lg:w-3/4 lg:justify-normal lg:flex lg:items-baseline gap-1",
              )}
            >
              <a
                data-testid={`${testId}-logo`}
                href="/"
                rel="home"
                title="Tabla"
                className={twMerge(
                  "lg:relative",
                  (!bigHeader || isScrolled) && "lg:contents",
                )}
              >
                <img
                  width={256}
                  height={85}
                  src={TablaLogo}
                  className={twMerge(
                    "w-26 h-xxm lg:w-auto lg:h-auto",
                    !bigHeader && "!w-26 !h-xxm",
                    (isScrolled || expandMenu) &&
                      "!h-7.3 !w-22 lg:!w-26 lg:!h-xxm transition-transform duration-75",
                  )}
                  alt="tabla"
                  loading="lazy"
                />
              </a>
              {(!bigHeader || isScrolled) && (
                <div className="hidden lg:block">
                  <HeaderMenuList
                    testId={`${testId}-main-links`}
                    items={HEADER_MAIN_MENU}
                    type="main"
                    scrolled={isScrolled}
                    bigHeader={bigHeader}
                  />
                </div>
              )}
              <div className="block lg:hidden content-center">
                <HeaderLeftWrapper
                  onClick={toggleExpanded}
                  searchExpanded={expanded}
                  toggleMobileMenu={toggleMobileMenu}
                  expandMenu={expandMenu}
                  showMenuIcon={showMenuIcon}
                />
              </div>
            </div>
            {!expandMenu && (
              <div
                className={twMerge(
                  "lg:float-right inline-grid lg:block px-4 lg:px-0 md:w-full md:justify-center lg:justify-start",
                  (!bigHeader || isScrolled) && "lg:w-1/4",
                )}
              >
                <div className="hidden lg:block">
                  <HeaderLeftWrapper
                    onClick={toggleExpanded}
                    searchExpanded={expanded}
                    toggleMobileMenu={toggleMobileMenu}
                    expandMenu={expandMenu}
                    showMenuIcon={showMenuIcon}
                    showUserIcon={isScrolled}
                  />
                </div>
                {bigHeader && !isScrolled && (
                  <HeaderMenuList
                    testId={`${testId}-main-links`}
                    items={HEADER_MAIN_MENU}
                    type="main"
                    bigHeader={bigHeader}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <SearchInput
        expanded={expanded}
        onScroll={isScrolled}
        initSearchValue={searchedText}
      />

      {/* Hamburger menu */}
      <HamburgerMenuModal expandMenu={expandMenu} />
    </>
  );
}
